import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import {
	IconTrendingUp,
	IconCash,
	IconPercentage,
	IconChartBar,
	IconDownload,
} from "@tabler/icons-react"
import { AppShell } from "@/components/layout/app-shell"
import { ProtectedRoute } from "@/components/layout/protected-route"
import { Button } from "@/components/ui/button"
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
	useMerchantCashFlowAnalytics,
	useMerchantCommissionAnalytics,
	useMerchantOverviewAnalytics,
	type Period,
} from "@/hooks/use-merchant-analytics"
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"

export const Route = createFileRoute("/analytics")({
	component: AnalyticsPage,
})

function AnalyticsPage() {
	return (
		<ProtectedRoute>
			<AppShell>
				<AnalyticsContent />
			</AppShell>
		</ProtectedRoute>
	)
}

function AnalyticsContent() {
	const [period, setPeriod] = useState<Period>("month")

	const { data: overview } = useMerchantOverviewAnalytics(period)
	const { data: cashFlow } = useMerchantCashFlowAnalytics(period)
	const { data: commission } = useMerchantCommissionAnalytics(period)

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-3xl font-bold tracking-tight">Analytics</h1>
					<p className="text-muted-foreground">
						Detailed reports and insights for your business
					</p>
				</div>
				<div className="flex items-center gap-4">
					<Select value={period} onValueChange={(v) => setPeriod(v as Period)}>
						<SelectTrigger className="w-[150px]">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="today">Today</SelectItem>
							<SelectItem value="week">This Week</SelectItem>
							<SelectItem value="month">This Month</SelectItem>
							<SelectItem value="all">All Time</SelectItem>
						</SelectContent>
					</Select>
					<Button variant="outline" size="sm">
						<IconDownload className="h-4 w-4 mr-2" />
						Export
					</Button>
				</div>
			</div>

			{/* Overview Stats */}
			{overview && (
				<div className="grid gap-4 md:grid-cols-4">
					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">Transactions</CardTitle>
							<IconChartBar className="h-4 w-4 text-muted-foreground" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold">{overview.transactions}</div>
							<p className="text-xs text-muted-foreground">
								{overview.successRate * 100}% success rate
							</p>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">Deposits</CardTitle>
							<IconTrendingUp className="h-4 w-4 text-green-600" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold text-green-600">
								৳{overview.cashFlow.deposits.toLocaleString()}
							</div>
							<p className="text-xs text-muted-foreground">
								Cash in from players
							</p>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">Payouts</CardTitle>
							<IconCash className="h-4 w-4 text-red-600" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold text-red-600">
								৳{overview.cashFlow.payouts.toLocaleString()}
							</div>
							<p className="text-xs text-muted-foreground">
								Cash out to players
							</p>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">Commission</CardTitle>
							<IconPercentage className="h-4 w-4 text-purple-600" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold text-purple-600">
								৳{overview.commission.total.toLocaleString()}
							</div>
							<p className="text-xs text-muted-foreground">
								{overview.commission.rate * 100}% rate
							</p>
						</CardContent>
					</Card>
				</div>
			)}

			{/* Detailed Analytics Tabs */}
			<Tabs defaultValue="cash-flow" className="space-y-4">
				<TabsList>
					<TabsTrigger value="cash-flow">Cash Flow</TabsTrigger>
					<TabsTrigger value="commission">Commission</TabsTrigger>
					<TabsTrigger value="channels">Channels</TabsTrigger>
				</TabsList>

				<TabsContent value="cash-flow" className="space-y-4">
					{cashFlow && (
						<>
							<div className="grid gap-4 md:grid-cols-2">
								<Card>
									<CardHeader>
										<CardTitle>Deposits</CardTitle>
										<CardDescription>Money received from players</CardDescription>
									</CardHeader>
									<CardContent>
										<div className="text-3xl font-bold text-green-600">
											৳{cashFlow.deposits.total.toLocaleString()}
										</div>
										<p className="text-sm text-muted-foreground mt-2">
											{cashFlow.deposits.count} transactions
										</p>
										<p className="text-xs text-muted-foreground">
											Average: ৳{cashFlow.deposits.average.toLocaleString()}
										</p>
									</CardContent>
								</Card>

								<Card>
									<CardHeader>
										<CardTitle>Payouts</CardTitle>
										<CardDescription>Money paid to players</CardDescription>
									</CardHeader>
									<CardContent>
										<div className="text-3xl font-bold text-red-600">
											৳{cashFlow.payouts.total.toLocaleString()}
										</div>
										<p className="text-sm text-muted-foreground mt-2">
											{cashFlow.payouts.count} transactions
										</p>
										<p className="text-xs text-muted-foreground">
											Average: ৳{cashFlow.payouts.average.toLocaleString()}
										</p>
									</CardContent>
								</Card>
							</div>

							<Card>
								<CardHeader>
									<CardTitle>Net Cash Flow</CardTitle>
									<CardDescription>Deposits minus payouts</CardDescription>
								</CardHeader>
								<CardContent>
									<div className={`text-3xl font-bold ${cashFlow.net >= 0 ? "text-green-600" : "text-red-600"}`}>
										৳{cashFlow.net.toLocaleString()}
									</div>
								</CardContent>
							</Card>

							<Card>
								<CardHeader>
									<CardTitle>By Channel</CardTitle>
									<CardDescription>Breakdown by payment operator</CardDescription>
								</CardHeader>
								<CardContent>
									<div className="space-y-4">
										{Object.entries(cashFlow.byChannel).map(([channel, data]) => {
											const total = data.deposits + data.payouts
											const depositPercent = total > 0 ? (data.deposits / total) * 100 : 0
											return (
												<div key={channel} className="space-y-2">
													<div className="flex items-center justify-between text-sm">
														<span className="capitalize font-medium">{channel}</span>
														<span className="text-muted-foreground">
															৳{data.deposits.toLocaleString()} in / ৳{data.payouts.toLocaleString()} out
														</span>
													</div>
													<div className="h-2 bg-muted rounded-full overflow-hidden flex">
														<div
															className="h-full bg-green-500"
															style={{ width: `${depositPercent}%` }}
														/>
														<div
															className="h-full bg-red-500"
															style={{ width: `${100 - depositPercent}%` }}
														/>
													</div>
												</div>
											)
										})}
									</div>
								</CardContent>
							</Card>
						</>
					)}
				</TabsContent>

				<TabsContent value="commission" className="space-y-4">
					{commission && (
						<>
							<div className="grid gap-4 md:grid-cols-3">
								<Card>
									<CardHeader>
										<CardTitle>Total Commission</CardTitle>
										<CardDescription>For selected period</CardDescription>
									</CardHeader>
									<CardContent>
										<div className="text-3xl font-bold text-purple-600">
											৳{commission.totalCommission.toLocaleString()}
										</div>
										<p className="text-xs text-muted-foreground mt-2">
											{commission.commissionRate * 100}% rate
										</p>
									</CardContent>
								</Card>

								<Card>
									<CardHeader>
										<CardTitle>Transactions</CardTitle>
										<CardDescription>Total eligible transactions</CardDescription>
									</CardHeader>
									<CardContent>
										<div className="text-3xl font-bold">
											{commission.transactions}
										</div>
										<p className="text-xs text-muted-foreground mt-2">
											Generated commission
										</p>
									</CardContent>
								</Card>

								<Card>
									<CardHeader>
										<CardTitle>Average per Transaction</CardTitle>
										<CardDescription>Commission earned per order</CardDescription>
									</CardHeader>
									<CardContent>
										<div className="text-3xl font-bold">
											৳{commission.transactions > 0
												? (commission.totalCommission / commission.transactions).toFixed(2)
												: "0.00"}
										</div>
									</CardContent>
								</Card>
							</div>

							{commission.breakdown && commission.breakdown.length > 0 && (
								<Card>
									<CardHeader>
										<CardTitle>Commission Breakdown</CardTitle>
										<CardDescription>Daily commission over time</CardDescription>
									</CardHeader>
									<CardContent>
										<ResponsiveContainer width="100%" height={300}>
											<AreaChart data={commission.breakdown}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis dataKey="date" />
												<YAxis />
												<Tooltip />
												<Area
													type="monotone"
													dataKey="commission"
													stroke="#8b5cf6"
													fill="#8b5cf6"
													fillOpacity={0.2}
												/>
											</AreaChart>
										</ResponsiveContainer>
									</CardContent>
								</Card>
							)}
						</>
					)}
				</TabsContent>

				<TabsContent value="channels" className="space-y-4">
					{cashFlow && (
						<Card>
							<CardHeader>
								<CardTitle>Channel Distribution</CardTitle>
								<CardDescription>Transaction volume by payment operator</CardDescription>
							</CardHeader>
							<CardContent>
								<ResponsiveContainer width="100%" height={300}>
									<BarChart data={Object.entries(cashFlow.byChannel).map(([channel, data]) => ({
										channel: channel.charAt(0).toUpperCase() + channel.slice(1),
										deposits: data.deposits,
										payouts: data.payouts,
									}))}>
										<CartesianGrid strokeDasharray="3 3" />
										<XAxis dataKey="channel" />
										<YAxis />
										<Tooltip />
										<Legend />
										<Bar dataKey="deposits" fill="#22c55e" name="Deposits" />
										<Bar dataKey="payouts" fill="#ef4444" name="Payouts" />
									</BarChart>
								</ResponsiveContainer>
							</CardContent>
						</Card>
					)}
				</TabsContent>
			</Tabs>
		</div>
	)
}
